import { Box, Typography, Grid, Paper, Tooltip } from '@mui/material';
import { AutoAwesome, Person2Rounded } from '@mui/icons-material';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setSelectedPlan } from 'store/fleet-planning.slice';
import { getPlanKpisWithComparison } from 'views/FleetPlanner/utils/dataFormatters';
import { type PlanComparisonKpi } from '../types/steps.types';
import useCustomerInfo from 'hooks/useCustomerInfo';

interface Plan {
  id: 'user' | 'optimized';
  title: string;
  description: string;
  kpis: PlanComparisonKpi[];
}

const KpiCard = ({
  kpi,
  id,
}: {
  kpi: PlanComparisonKpi;
  id: 'user' | 'optimized';
}) => {
  const { enableDarkTheme } = useAppTheme();
  const isHighlighted = id === 'optimized' && kpi.key === 'production_rate';

  const breakdownContent = kpi.breakdown
    ? Object.entries(kpi.breakdown).map(([key, val]) => (
        <Typography key={key} variant="caption" display="block">
          {key}: {val}
        </Typography>
      ))
    : null;

  return (
    <Tooltip
      title={breakdownContent ? <Box sx={{}}>{breakdownContent}</Box> : ''}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: enableDarkTheme ? 'grey.800' : 'grey.200',
            color: enableDarkTheme ? 'grey.100' : 'grey.900',
            fontSize: '0.75rem',
            borderRadius: 1,
            boxShadow: 2,
          },
        },
        arrow: {
          sx: {
            color: enableDarkTheme ? 'grey.800' : 'grey.200',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: 1.8,
          borderRadius: 2,
          cursor: 'default',
          bgcolor: isHighlighted
            ? enableDarkTheme
              ? 'rgba(34,197,94,0.2)'
              : 'rgba(187,247,208,0.4)'
            : 'transparent',
          transition: 'background 0.2s ease',
          '&:hover': {
            bgcolor: isHighlighted
              ? enableDarkTheme
                ? 'rgba(34,197,94,0.3)'
                : 'rgba(187,247,208,0.6)'
              : enableDarkTheme
              ? 'rgba(255,255,255,0.04)'
              : 'rgba(0,0,0,0.02)',
          },
          border: enableDarkTheme ? '1px solid #4E342E' : '1px solid #E0E0E0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <kpi.icon
            fontSize="small"
            color={isHighlighted ? 'success' : 'action'}
          />
          <Typography
            sx={{
              fontWeight: isHighlighted ? 600 : 500,
              color: isHighlighted
                ? enableDarkTheme
                  ? 'rgb(134, 239, 172)'
                  : 'rgb(21, 128, 61)'
                : enableDarkTheme
                ? 'grey.300'
                : 'grey.800',
              fontSize: '0.8rem',
            }}
          >
            {kpi.label}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.8rem',
              color: enableDarkTheme ? 'white' : 'black',
            }}
          >
            {kpi.value}
          </Typography>
          {kpi.delta && kpi.showDelta && (
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                color:
                  Number(kpi.delta) > 0
                    ? kpi.deltaColors?.up ?? 'inherit'
                    : kpi.deltaColors?.down ?? 'inherit',
              }}
            >
              ({kpi.delta})
            </Typography>
          )}
          {kpi?.unit && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: enableDarkTheme ? 'grey.400' : 'grey.600',
              }}
            >
              {kpi.unit ?? ''}
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

const PlanCard = ({
  plan,
  isActive,
  onClick,
}: {
  plan: Plan;
  isActive: boolean;
  onClick: (id: 'user' | 'optimized') => void;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const Icon = plan.id === 'user' ? Person2Rounded : AutoAwesome;
  const iconColor =
    plan.id === 'user'
      ? enableDarkTheme
        ? 'rgb(96, 165, 250)'
        : 'rgb(37, 99, 235)'
      : enableDarkTheme
      ? 'rgb(250, 204, 21)'
      : 'rgb(202, 138, 4)';

  return (
    <Paper
      onClick={() => {
        onClick(plan.id);
      }}
      sx={{
        p: 1.5,
        borderRadius: 3,
        cursor: 'pointer',
        border: isActive ? '2px dashed' : '2px solid ',
        borderColor: isActive
          ? 'primary.main'
          : enableDarkTheme
          ? 'grey.700'
          : 'grey.300',
        transition: 'all 0.3s ease-in-out',
        boxShadow: isActive
          ? enableDarkTheme
            ? '0px 0px 15px rgba(59, 130, 246, 0.4)'
            : '0px 0px 15px rgba(37, 99, 235, 0.4)'
          : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
      elevation={0}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            {plan.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: enableDarkTheme ? 'grey.400' : 'grey.600',
              fontSize: '14px',
            }}
          >
            {plan.description}
          </Typography>
        </Box>
        <Icon style={{ width: 32, height: 32, color: iconColor }} />
      </Box>
      {plan.kpis && (
        <Box
          sx={{
            backgroundColor: enableDarkTheme
              ? 'rgba(17, 24, 39, 0.5)'
              : '#F5F5F5',
            borderRadius: 2,
            p: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)', // two columns
            gap: 1,
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          {plan.kpis.map((kpi, index) => (
            <Box
              key={kpi.label}
              sx={{
                gridColumn: index === 0 ? '1 / -1' : 'auto', // first item spans full width
              }}
            >
              <KpiCard kpi={kpi} id={plan.id} />
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

// titles/descriptions are set dynamically inside PlanComparison using t()
const userPlanDummyData: Omit<Plan, 'title' | 'description'> = {
  id: 'user',
  kpis: [],
};
const optimizedPlanDummyData: Omit<Plan, 'title' | 'description'> = {
  id: 'optimized',
  kpis: [],
};

export const PlanComparison = () => {
  const { t } = useTranslation();
  const { unitSystem } = useCustomerInfo();
  const userPlanMetrics = useAppSelector(
    (state) => state.fleetPlanningReducer.userPlan.metrics
  );
  const optimizedPlanMetrics = useAppSelector(
    (state) => state.fleetPlanningReducer.optimizedPlan.metrics
  );
  const selectedPlan = useAppSelector(
    (state) => state.fleetPlanningReducer.selectedPlan
  );
  const dispatch = useAppDispatch();

  const handleClickPlan = (newPlan: 'user' | 'optimized') => {
    dispatch(setSelectedPlan(newPlan));
  };

  const userKpis = getPlanKpisWithComparison(userPlanMetrics, unitSystem);
  const optimizedKpis = getPlanKpisWithComparison(
    optimizedPlanMetrics,
    unitSystem,
    userPlanMetrics
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <PlanCard
          plan={{
            ...userPlanDummyData,
            title: t('cdp_custom_payload_plan_title'),
            description: t('cdp_custom_payload_plan_description'),
            kpis: userKpis,
          }}
          isActive={selectedPlan === 'user'}
          onClick={() => {
            handleClickPlan('user');
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PlanCard
          plan={{
            ...optimizedPlanDummyData,
            title: t('cdp_max_capacity_plan_title'),
            description: t('cdp_max_capacity_plan_description'),
            kpis: optimizedKpis,
          }}
          isActive={selectedPlan === 'optimized'}
          onClick={() => {
            handleClickPlan('optimized');
          }}
        />
      </Grid>
    </Grid>
  );
};
